import React, { useState, useEffect } from 'react';
import { updateEvent } from '../apis/eventsApi';
import { notification, Button, Radio, Input, InputNumber, DatePicker, Checkbox, Space, Tooltip, Upload, Select } from 'antd';
import { QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { uploadImage, fetchImages } from '../apis/uploadApi';
import EventForm from './EventForm';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function EventEditModal({ event, onSuccess }) {
  const [scope, setScope] = useState('one');
  const [newStart, setNewStart] = useState(null);
  const [newEnd, setNewEnd] = useState(null);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newImage, setNewImage] = useState('');
  const [newAdditionalInfo, setNewAdditionalInfo] = useState('');
  const [newPlace, setNewPlace] = useState('');
  const [newQuotas, setNewQuotas] = useState(null);
  const [newPrice, setNewPrice] = useState(null);
  const [newPriceNoImpl, setNewPriceNoImpl] = useState(null);
  const [newExtraPriceMembers, setNewExtraPriceMembers] = useState(null);
  const [discountsSession, setDiscountsSession] = useState(null);
  const [enabledForCourtesy, setEnabledForCourtesy] = useState(false);
  const [enabledScheduleRestrictions, setEnabledScheduleRestrictions] = useState(true);
  const [skipsClosures, setSkipsClosures] = useState(false);
  const [reschedule, setReschedule] = useState(true);
  const [loading, setLoading] = useState(false);
  const [availableImages, setAvailableImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const resolveImageSrc = (imageValue) => {
    if (!imageValue) return '';

    const value = typeof imageValue === 'string'
      ? imageValue
      : imageValue.url || imageValue.path || imageValue.filename || '';

    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/') || value.startsWith('data:') || value.startsWith('blob:')) {
      return value;
    }
    if (value.startsWith('img/')) return `/${value}`;
    return `/img/${value}`;
  };

  useEffect(() => {
    const loadImgs = async () => {
      try {
        const imgRes = await fetchImages();
        if (imgRes.success) setAvailableImages(imgRes.images || []);
      } catch (e) {
        console.error('Error loading images', e);
      }
    };
    loadImgs();
  }, []);

  const handleBeforeUpload = (file) => {
    setSelectedFile(file);
    const tempImg = { url: URL.createObjectURL(file), name: `[Nueva] ${file.name}`, isTemp: true };
    setAvailableImages(prev => {
      const filtered = prev.filter(img => !img.isTemp);
      return [...filtered, tempImg];
    });
    setNewImage(file.name);
    return false; // Prevent automatic upload
  };

  // Pre-populate when event changes
  // Convert displayed price from cents to pesos
  useEffect(() => {
    if (event) {
      setScope('one');
      setNewStart(event.start_date ? event.start_date.tz(dayjs.tz.guess()) : null);
      setNewEnd(event.end_date ? event.end_date.tz(dayjs.tz.guess()) : null);
      setReschedule(true);
      setNewName(event.name || '');
      setNewDescription(event.description || '');
      setNewImage(event.image || '');
      setNewAdditionalInfo(event.aditional_info || '');
      setNewPlace(event.place || '');
      setNewQuotas(event.quotas ?? null);
      setNewPrice(event.price_in_cents ? event.price_in_cents / 100 : null);
      setNewPriceNoImpl(event.price_in_cents_without_implements ? event.price_in_cents_without_implements / 100 : null);
      setNewExtraPriceMembers(event.extra_price_in_cents_for_members ? event.extra_price_in_cents_for_members / 100 : null);
      setDiscountsSession(event.discounts_session ?? null);
      setEnabledForCourtesy(event.enabled_for_courtesy ?? false);
      setEnabledScheduleRestrictions(event.enabled_schedule_restrictions ?? true);
      setSkipsClosures(event.skips_closures ?? false);
    }
  }, [event]);

  const handleSubmit = async () => {
    if (!event?.event_id) {
      notification.warning({ message: 'No se ha seleccionado un evento' });
      return;
    }

    const hasStartChanged = !event.start_date || !newStart || !newStart.isSame(event.start_date, 'minute');
    if (hasStartChanged && newStart && newStart.isBefore(dayjs().startOf('day'))) {
      notification.warning({ message: 'El nuevo inicio no puede ser antes de hoy' });
      return;
    }

    if (newStart && newEnd && newEnd.isBefore(newStart)) {
      notification.warning({ message: 'El nuevo fin no puede ser antes del nuevo inicio' });
      return;
    }

    // For is_edited events, we need the original_start_date (not the rescheduled start_date)
    // to locate the recurring_exception on the backend
    const occurrenceDate = event.is_edited && event.original_start_date
      ? (typeof event.original_start_date === 'string'
          ? event.original_start_date
          : event.original_start_date?.format?.('YYYY-MM-DD HH:mm:ss'))
      : event.start_date?.format('YYYY-MM-DD HH:mm:ss');
    const payload = {};

    if (scope === 'one') {
      payload.original_start_date = occurrenceDate;
      payload.new_start_date = newStart ? newStart.utc().format('YYYY-MM-DD HH:mm:ss') : null;
      payload.new_end_date = newEnd ? newEnd.utc().format('YYYY-MM-DD HH:mm:ss') : null;
      payload.reschedule = reschedule;
    } else if (scope === 'from_now') {
      payload.occurrence_date = occurrenceDate;
    }

    // Optional fields (nullable)
    payload.new_name = newName || null;
    payload.new_description = newDescription || null;
    payload.new_image = newImage || null;
    payload.new_aditional_info = newAdditionalInfo || null;
    payload.new_place = newPlace || null;
    payload.new_quotas = newQuotas !== null ? newQuotas : null;
    // Convert displayed pesos back to cents for backend
    payload.new_price_in_cents = newPrice !== null ? Math.round(newPrice * 100) : null;
    payload.new_price_in_cents_without_implements = newPriceNoImpl !== null ? Math.round(newPriceNoImpl * 100) : null;
    payload.new_extra_price_in_cents_for_members = newExtraPriceMembers !== null ? Math.round(newExtraPriceMembers * 100) : null;
    payload.discounts_session = discountsSession !== null ? discountsSession : null;

    // Non-nullable fields must always be sent
    payload.enabled_for_courtesy = enabledForCourtesy ? 1 : 0;
    payload.enabled_schedule_restrictions = enabledScheduleRestrictions ? 1 : 0;
    payload.skips_closures = skipsClosures ? 1 : 0;

    setLoading(true);
    try {
      let finalPayload = { ...payload };
      if (selectedFile) {
        setUploadingImage(true);
        const res = await uploadImage(selectedFile);
        if (res.success) {
          if (scope === 'one') {
            finalPayload.new_image = res.url;
          }
        } else {
          throw new Error('Error al subir imagen antes de guardar');
        }
      }
      await updateEvent(event.event_id, finalPayload, scope, event.rule_id, event.is_unique, event.is_edited);
      notification.success({ message: 'Evento actualizado' });
      setSelectedFile(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error al actualizar evento',
        description: err.response?.data?.error || err.message,
      });
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  if (!event) return null;

  const handleFromNowSubmit = async (payload) => {
    if (!event?.event_id) return;
    const occurrenceDate = event.start_date?.format('YYYY-MM-DD HH:mm:ss');
    // We add occurrence_date so the backend knows where to split
    const updatePayload = {
      ...payload,
      occurrence_date: occurrenceDate,
      rule_id: event.rule_id
    };

    setLoading(true);
    try {
      await updateEvent(event.event_id, updatePayload, 'from_now', event.rule_id);
      notification.success({ message: 'Eventos creados y actualizados correctamente' });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error al actualizar evento a futuro',
        description: err.response?.data?.error || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p style={{ marginBottom: 16, color: '#888' }}>
        Evento: <strong>{event.name}</strong> — {event.start_date?.tz(dayjs.tz.guess()).format('dddd D [de] MMMM, HH:mm')}
      </p>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Alcance de la edición:</label>
        <Radio.Group value={scope} onChange={e => setScope(e.target.value)}>
          <Space direction="vertical">
            <Radio value="one">Solo esta ocurrencia</Radio>
            <Radio value="from_now" disabled={event.is_unique}>
              Desde esta fecha en adelante
              {event.is_unique && (
                <Tooltip title="Esta opción solo es válida para eventos recurrentes">
                  <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              )}
            </Radio>
          </Space>
        </Radio.Group>
      </div>

      {scope === 'from_now' ? (
        <div style={{ marginTop: 24, borderTop: '1px solid #eee', paddingTop: 24 }}>
          <p style={{ marginBottom: 16, color: '#666' }}>
            Se detendrá la repetición del evento original y se creará un nuevo evento a partir de esta fecha con la siguiente información:
          </p>
          <EventForm 
            initialData={{
              ...event,
              first_recurrence: event.start_date?.utc().format('YYYY-MM-DDTHH:mm:ss')
            }}
            onSuccess={onSuccess}
            onCustomSubmit={handleFromNowSubmit}
            hideTypeSelector={true}
            isModal={true}
          />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>Nuevo inicio:</label>
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={newStart}
              onChange={setNewStart}
              style={{ width: '100%' }}
              needConfirm={false}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>Nuevo fin:</label>
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={newEnd}
              onChange={setNewEnd}
              style={{ width: '100%' }}
              needConfirm={false}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <Checkbox checked={reschedule} onChange={e => setReschedule(e.target.checked)}>
              Reagendar asistentes automáticamente
            </Checkbox>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label>Nombre:</label>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre del evento" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Descripción:</label>
            <Input.TextArea rows={3} value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="Descripción" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>Imagen:</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                style={{ flex: 1 }}
                placeholder="Selecciona una imagen existente"
                value={newImage || undefined}
                onChange={value => {
                  setNewImage(value);
                  if (value !== selectedFile?.name) {
                    setSelectedFile(null);
                  }
                }}
                options={availableImages.map(img => ({
                  value: img.url,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={resolveImageSrc(img.url)} alt={img.name} style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4 }} />
                      <span>{img.name}</span>
                    </div>
                  ),
                  name: img.name
                }))}
                filterOption={(input, option) =>
                  (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                showSearch
              />
              <Upload beforeUpload={handleBeforeUpload} showUploadList={false} accept="image/*">
                <Button icon={<UploadOutlined />} loading={uploadingImage}>Subir</Button>
              </Upload>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Info adicional:</label>
            <Input value={newAdditionalInfo} onChange={e => setNewAdditionalInfo(e.target.value)} placeholder="Información adicional" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Lugar:</label>
            <Input value={newPlace} onChange={e => setNewPlace(e.target.value)} placeholder="Lugar del evento" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Capacidad (quotas):</label>
            <InputNumber style={{ width: '100%' }} value={newQuotas} onChange={value => setNewQuotas(value)} min={0} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Precio:
              <InputNumber style={{ width: '100%' }} value={newPrice} onChange={value => setNewPrice(value)} min={0} formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''} parser={value => value ? value.replace(/\./g, '') : ''} />
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Precio sin implementos:
              <InputNumber style={{ width: '100%' }} value={newPriceNoImpl} onChange={value => setNewPriceNoImpl(value)} min={0} formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''} parser={value => value ? value.replace(/\./g, '') : ''} />
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label>Precio extra para miembros:
              <InputNumber style={{ width: '100%' }} value={newExtraPriceMembers} onChange={value => setNewExtraPriceMembers(value)} min={0} formatter={value => value ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''} parser={value => value ? value.replace(/\./g, '') : ''} />
            </label>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Checkbox checked={discountsSession ?? false} onChange={e => setDiscountsSession(e.target.checked ? 1 : 0)}>
              Descuenta sesión
            </Checkbox>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Checkbox checked={enabledForCourtesy} onChange={e => setEnabledForCourtesy(e.target.checked)}>
              Habilitada para cortesías
            </Checkbox>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Checkbox checked={enabledScheduleRestrictions} onChange={e => setEnabledScheduleRestrictions(e.target.checked)}>
              Restringir agendamiento según membresía
              <Tooltip title="Si está marcado, por ejemplo la membresía morning solo puede agendar este evento si está entre las 6 y las 9 am.">
                <QuestionCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </Checkbox>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Checkbox checked={skipsClosures} onChange={e => setSkipsClosures(e.target.checked)}>
              Saltar cierres de sede
              <Tooltip title="Si está marcado, este evento se llevará a cabo incluso en días que la sede esté marcada como cerrada (ej. festivos)">
                <QuestionCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
            </Checkbox>
          </div>

          <div style={{ textAlign: 'right' }}>
            <Button type="primary" onClick={handleSubmit} loading={loading}>Guardar Cambios</Button>
          </div>
        </>
      )}
    </div>
  );
}
